import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { logoIcon, useSiteContent } from "../../lib/site";
import { LEGAL_CONTENT, getPageUrl } from "./content";

export function PublicShell({ title, subtitle, children, testId }) {
    const [pages, setPages] = useState([]);
    const siteContent = useSiteContent();
    useEffect(() => {
        window.scrollTo(0, 0);
        api.get("/pages").then((r) => setPages(r.data)).catch(() => {});
    }, []);
    return (
        <div className="min-h-screen bg-background" data-testid={testId || "public-page"}>
            <header className="sticky top-0 z-40 border-b border-stone-800 bg-stone-950/95 text-stone-100 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="public-logo">
                        <img src={logoIcon(siteContent)} alt="ATA Angora" className="h-8 w-8 object-contain" />
                        <span className="font-display text-lg font-bold tracking-tight">
                            <span className="text-terra-500">ata</span> <span className="text-emerald-400">angora</span>
                        </span>
                    </Link>
                    <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-stone-300 transition-colors hover:text-white" data-testid="public-home-link">
                        <ArrowLeft className="h-3.5 w-3.5" /> Ana Sayfa
                    </Link>
                </div>
            </header>
            <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
                {title && <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl" data-testid="public-page-title">{title}</h1>}
                {subtitle && <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{subtitle}</p>}
                {children}
            </main>
            <footer className="border-t py-8">
                <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-5 gap-y-2 px-4 sm:px-6">
                    {pages.map((p) => (
                        <Link key={p.page_id} to={getPageUrl(p)} className="text-xs text-muted-foreground transition-colors hover:text-primary">
                            {p.title}
                        </Link>
                    ))}
                    <Link to="/iletisim" className="text-xs text-muted-foreground transition-colors hover:text-primary">İletişim</Link>
                </div>
                <p className="mx-auto mt-4 max-w-5xl px-4 text-[11px] text-stone-400 sm:px-6">© 2026 ATA Angora Tedarik Platformu — Ankara</p>
            </footer>
        </div>
    );
}

export default function LegalPage({ slug: slugProp }) {
    const params = useParams();
    const slug = slugProp || params.slug;
    const [page, setPage] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        setPage(null);
        setMissing(false);
        api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch((e) => {
            if (e.response?.status === 404) {
                setMissing(true);
            } else if (LEGAL_CONTENT[slug]) {
                setPage(LEGAL_CONTENT[slug]);
            } else {
                setMissing(true);
            }
        });
    }, [slug]);

    if (missing) return <Navigate to="/" replace />;
    if (!page) {
        return (
            <PublicShell testId={`legal-page-${slug}`}>
                <div className="mt-8 h-64 animate-pulse rounded-2xl bg-muted" />
            </PublicShell>
        );
    }
    return (
        <PublicShell title={page.title} subtitle={`Son güncelleme: ${page.updated}`} testId={`legal-page-${slug}`}>
            <div className="mt-8 space-y-8" data-testid={`legal-content-${slug}`}>
                {page.sections.map((s, i) => (
                    <section key={i}>
                        <h2 className="font-display text-lg font-bold">{s.h}</h2>
                        <div className="mt-2 space-y-2 text-sm leading-relaxed text-muted-foreground">
                            {s.p.map((par, j) => <p key={j}>{par}</p>)}
                        </div>
                    </section>
                ))}
            </div>
        </PublicShell>
    );
}
